import { GetStaticPropsResult } from 'next';

import { Box, Flex } from '@chakra-ui/react';

import { PageCategory } from '~/features/analytics/constants';
import {
  CatalogFilterDefinitionData,
  getCatalogFilterDataFromServer,
} from '~/features/catalog/controllers/get-catalog-filter-data-from-server';
import { getCatalogSearchResults } from '~/features/catalog/controllers/get-catalog-search-results';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { DEFAULT_SORT, UriFilter } from '~/features/catalog/models/filter';
import { Filters } from '~/features/catalog/models/filter';
import { Catalog } from '~/features/experiments/ab_plp_redesign_2025/components/catalog';
import { CatalogHeroSection } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-hero-section';
import {
  CatalogSubcategoriesSection,
  StaticCatalogSubcategoriesSectionProps,
  transformStaticCatalogSubcategoriesSectionProps,
} from '~/features/experiments/ab_plp_redesign_2025/components/catalog-subcategories-section';
import { useExperimentalSegmentEvents } from '~/features/experiments/ab_plp_redesign_2025/hooks';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { useSessionReplay } from '~/features/session-replay';
import { useI18n } from '~/hooks/use-i18n';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

type CatalogPageProps = {
  page: Page;
  catalogSearchResults: CatalogSearchResults | null;
  skillUriFilters: UriFilter[];
  schoolUriFilters: UriFilter[];
  staticCatalogSubcategoriesSectionProps: StaticCatalogSubcategoriesSectionProps;
};

export default function CatalogPage(props: CatalogPageProps) {
  const { isTranslated } = useI18n();
  const experimentalSegmentEvents = useExperimentalSegmentEvents('dino');

  useSessionReplay();

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.Catalog,
    },
    isEnabled: experimentalSegmentEvents?.variant === 'on',
  });

  const { page, catalogSearchResults, schoolUriFilters, skillUriFilters, staticCatalogSubcategoriesSectionProps } =
    props as CatalogPageProps;

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Catalog} />
      <PageLayout withoutVerticalPadding>
        <Box bgColor='grey.50' paddingBottom={{ base: '56px', md: '112px' }}>
          <CatalogHeroSection />
          <Flex
            flexDirection='column'
            paddingTop={{ base: '24px', md: '40px' }}
            gap={{
              base: '56px',
              md: '112px',
            }}
          >
            <Catalog
              initialCatalogSearchResults={catalogSearchResults}
              schoolUriFilters={schoolUriFilters}
              skillUriFilters={skillUriFilters}
            />
            {!isTranslated && <CatalogSubcategoriesSection {...staticCatalogSubcategoriesSectionProps} />}
          </Flex>
        </Box>
      </PageLayout>
    </>
  );
}

export async function getStaticProps(): Promise<GetStaticPropsResult<CatalogPageProps>> {
  // Use en-us locale metadata as default (client-side hook handles locale-specific data)
  const defaultLocaleMetadata = getCatalogFilterDataFromServer('en-us') as CatalogFilterDefinitionData;
  const skillUriFilters = defaultLocaleMetadata?.skills || [];
  const schoolUriFilters = defaultLocaleMetadata?.schools || [];

  const [page, staticCatalogSubcategoriesSectionProps] = await Promise.all([
    getPage('/catalog'),
    transformStaticCatalogSubcategoriesSectionProps(),
  ]);

  const catalogSearchResults = await getCatalogSearchResults({
    selectedFilters: {} as Filters,
    selectedSort: DEFAULT_SORT,
    page: 0,
    schoolUriFilters,
    skillUriFilters,
  });

  return {
    props: {
      page,
      catalogSearchResults,
      skillUriFilters,
      schoolUriFilters,
      staticCatalogSubcategoriesSectionProps,
    },
  };
}
